import { ComboboxData, ComboboxItem } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { UseQueryResult } from "@tanstack/react-query"
import { PaginationType } from "@tfg/types/schemas/utils"
import { useEffect, useState } from "react"

import { DEFAULT_PAGE_SIZE } from "@tfg/ui"

import { SelectProps, Select } from "../../../molecules/Inputs/Select/Select"

type AutocompleteProps<T, QueryParams extends PaginationType> = {
  value: string | null
  extraParams?: Omit<QueryParams, "pagination" | "searchParams">
  onChange: (newValue: string | null, option: ComboboxItem) => void
  onCreate: (query: string) => void
  parseOptionFunction: (data: T[]) => {
    label: string
    value: string
  }[]
  pageSize?: number
  isLoading?: boolean
  query: (
    args: QueryParams
  ) => Pick<
    UseQueryResult<T[], unknown>,
    "data" | "isPending" | "isFetching" | "isError" | "error"
  >
} & Omit<SelectProps, "onCreate" | "onChange">

export const AutoCompleteWithQuery = <T, QueryParams extends PaginationType>({
  onCreate,
  onChange,
  value,
  query,
  parseOptionFunction,
  pageSize = DEFAULT_PAGE_SIZE,
  extraParams,
  ...props
}: AutocompleteProps<T, QueryParams>) => {
  const [selectOptions, setSelectOptions] = useState<ComboboxData>([])
  const [search, setSearch] = useState<string | undefined>()
  const [debouncedSearch] = useDebouncedValue(search, 300)

  const { data, isPending: isQueryLoading } = query({
    searchParams: debouncedSearch,
    pagination: {
      page: 0,
      pageSize,
    },
    ...(extraParams ?? {}),
  } as QueryParams)

  useEffect(() => {
    if (!isQueryLoading && data) {
      const parsedOptions = parseOptionFunction(data) || []
      setSelectOptions(parsedOptions)
    }
  }, [data, parseOptionFunction, isQueryLoading])

  return (
    <Select
      data={selectOptions}
      withAsterisk={props.required}
      isLoading={isQueryLoading}
      creatable
      searchable
      searchValue={search}
      clearable
      onSearchChange={(value) => {
        setSearch(value)
      }}
      onCreate={(query?: string | null) => onCreate(query ?? "")}
      onChange={(newValue, option) => {
        if (onChange) {
          onChange(newValue, option)
        }
      }}
      value={value}
      {...props}
    />
  )
}
